import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import {
  Bar,
  BarChart,
  BarXAxis,
  ChartTooltip,
  Grid,
  Line,
  LineChart,
  type PieData,
  PieCenter,
  PieChart,
  PieSlice,
  XAxis,
} from "@/components/charts";
import type { AnalyticsSummary } from "./analytics.hooks";

const SLICE_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

const pesos = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  maximumFractionDigits: 0,
});

function hubLineColor(index: number): string {
  if (index < SLICE_COLORS.length) return SLICE_COLORS[index] ?? "var(--chart-1)";
  return `hsl(${(index * 47 + 158) % 360} 56% 42%)`;
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <SurfaceCard render={<section />}>
      <header className="mb-3">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      </header>
      {children}
    </SurfaceCard>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

function HubEarningsChart({ data }: { data: AnalyticsSummary }) {
  const hasEarnings = data.hubEarningsSeries.some((series) =>
    data.hubEarningsByDay.some((day) => Number(day[series.key] ?? 0) > 0),
  );

  if (!hasEarnings) {
    return <EmptyChart message="No completed-session earnings in this range." />;
  }

  return (
    <>
      <LineChart data={data.hubEarningsByDay} aspectRatio="2.4 / 1">
        <Grid horizontal />
        {data.hubEarningsSeries.map((series, index) => (
          <Line
            dataKey={series.key}
            fadeEdges={false}
            key={series.key}
            showHighlight
            stroke={hubLineColor(index)}
            strokeWidth={2}
          />
        ))}
        <XAxis numTicks={4} />
        <ChartTooltip
          rows={(point) =>
            data.hubEarningsSeries
              .map((series, index) => ({
                color: hubLineColor(index),
                label: series.name,
                value: Number(point[series.key] ?? 0),
              }))
              .map((row) => ({ ...row, value: pesos.format(row.value) }))
          }
        />
      </LineChart>

      <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
        {data.hubEarningsSeries.map((series, index) => (
          <li className="flex items-center gap-1.5 text-xs text-muted-foreground" key={series.key}>
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: hubLineColor(index) }}
            />
            <span>{series.name}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The chart layer answers five scan-first questions: is demand moving, how
 * is every hub earning, which hubs lead volume, which treatments are most
 * booked, and how are customers paying.
 */
export function AnalyticsCharts({ data }: { data: AnalyticsSummary }) {
  const hubBars = data.hubPerformance.slice(0, 6);
  const massageTypes = data.massagePerformance.slice(0, 6);
  const paymentSlices: PieData[] = data.paymentBreakdown.map((payment, index) => ({
    label: payment.method,
    value: payment.count,
    color: SLICE_COLORS[index % SLICE_COLORS.length],
  }));
  const massageSlices: PieData[] = massageTypes.map((massage, index) => ({
    label: massage.name,
    value: massage.bookings,
    color: hubLineColor(index),
  }));
  const hasBookingActivity = data.bookingsByDay.some(
    (day) => day.bookings > 0 || day.cancelledBookings > 0,
  );

  return (
    <section className="space-y-5">
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Booking demand"
          subtitle="Non-cancelled and cancelled bookings by scheduled day"
        >
          {hasBookingActivity ? (
            <>
              <LineChart data={data.bookingsByDay} aspectRatio="2.4 / 1">
                <Grid horizontal />
                <Line dataKey="bookings" stroke="var(--chart-line-primary)" />
                <Line dataKey="cancelledBookings" stroke="var(--chart-5)" />
                <XAxis numTicks={4} />
                <ChartTooltip
                  rows={(point) => [
                    {
                      color: "var(--chart-line-primary)",
                      label: "Non-cancelled bookings",
                      value: Number(point.bookings ?? 0),
                    },
                    {
                      color: "var(--chart-5)",
                      label: "Cancelled bookings",
                      value: Number(point.cancelledBookings ?? 0),
                    },
                  ]}
                />
              </LineChart>
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-chart-1" />
                  Non-cancelled bookings
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-chart-5" />
                  Cancelled bookings
                </span>
              </div>
            </>
          ) : (
            <EmptyChart message="No bookings in this range." />
          )}
        </ChartCard>

        <ChartCard
          title="Hub earnings"
          subtitle="Completed-session value per hub, by scheduled day"
        >
          <HubEarningsChart data={data} />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <ChartCard title="Hub contribution" subtitle="Top hubs by non-cancelled bookings">
          {hubBars.length > 0 ? (
            <BarChart data={hubBars} xDataKey="name" aspectRatio="2.35 / 1">
              <Grid horizontal />
              <Bar dataKey="bookings" fill="var(--chart-line-primary)" />
              <BarXAxis />
              <ChartTooltip />
            </BarChart>
          ) : (
            <EmptyChart message="No hub activity in this range." />
          )}
        </ChartCard>

        <ChartCard title="Payment mix" subtitle="Share of non-cancelled bookings">
          {paymentSlices.length > 0 ? (
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <PieChart data={paymentSlices} size={176} innerRadius={46}>
                {paymentSlices.map((_, index) => (
                  <PieSlice key={index} index={index} />
                ))}
                <PieCenter defaultLabel="Bookings" />
              </PieChart>
              <ul className="w-full space-y-2 sm:max-w-44">
                {data.paymentBreakdown.slice(0, 4).map((payment, index) => (
                  <li
                    className="flex items-center justify-between gap-3 text-xs"
                    key={payment.method}
                  >
                    <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: SLICE_COLORS[index % SLICE_COLORS.length] }}
                      />
                      <span className="truncate capitalize">{payment.method}</span>
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums text-foreground">
                      {payment.percentage}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <EmptyChart message="No bookings in this range." />
          )}
        </ChartCard>

        <ChartCard
          title="Most-booked massage types"
          subtitle="Ranked by non-cancelled bookings · price is the average booking-time value"
        >
          {massageSlices.length > 0 ? (
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center xl:flex-col">
              <PieChart data={massageSlices} size={154} innerRadius={40}>
                {massageSlices.map((_, index) => (
                  <PieSlice key={index} index={index} />
                ))}
                <PieCenter defaultLabel="Bookings" />
              </PieChart>
              <ul className="w-full space-y-2 sm:max-w-48 xl:max-w-none">
                {massageTypes.map((massage, index) => (
                  <li
                    className="flex items-center justify-between gap-3 text-xs"
                    key={massage.name}
                  >
                    <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: hubLineColor(index) }}
                      />
                      <span className="truncate">{massage.name}</span>
                    </span>
                    <span className="shrink-0 text-right font-semibold tabular-nums text-foreground">
                      {pesos.format(massage.averageBookedPrice)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <EmptyChart message="No massage bookings in this range." />
          )}
        </ChartCard>
      </div>
    </section>
  );
}
